import { useEffect, useMemo, useState } from "react";
import { Box, Checkbox, Stack, Typography } from "@mui/material";
import { AppModal } from "global/components/AppModal";
import EmptyState from "global/components/EmptyState/EmptyState";
import SettingsSearchField from "screens/Settings/components/SettingsSearchField";
import { useAppSelector } from "hooks/use-app-select";
import {
  SeatReductionRequired,
  SeatReductionUser,
  userStatus,
} from "types/perSeat";
import {
  avatarSx,
  checkboxSx,
  emailSx,
  emptyStateSx,
  introSx,
  listSx,
  lockedCheckboxSx,
  meterFillSx,
  meterTrackSx,
  nameSx,
  rowSx,
  searchSx,
  statusSx,
  summaryCountSx,
  summaryHintSx,
  summaryRowSx,
  summarySx,
} from "./CustomSeatReductionDialog.styles";

interface CustomSeatReductionDialogProps {
  /** `null` closes the dialog. */
  reduction: SeatReductionRequired | null;
  /**
   * Seats the customer is actually buying. When it is fewer than the plan's
   * cap, that is how many users they may keep — paying for 3 seats and keeping
   * 5 people would leave more users than seats.
   */
  purchasedSeats?: number;
  isSubmitting?: boolean;
  /** Receives exactly the number of ids the customer is allowed to keep. */
  onConfirm: (keepUserIds: number[]) => void;
  onClose: () => void;
}

/** Search is noise on a short list and essential on a long one. */
const SEARCHABLE_FROM = 8;

const statusLabel = (user: SeatReductionUser) =>
  user.status === userStatus.pendingVerification ? "Invited" : user.status;

const initials = (user: SeatReductionUser) => {
  const source = (user.name || user.username || user.email || "?").trim();
  const [first = "", second = ""] = source.split(/\s+/);
  return `${first.charAt(0)}${second.charAt(0)}` || "?";
};

/**
 * Downgrade step: the seats being bought are fewer than the people on the
 * account, so the customer picks exactly who keeps a seat. Everyone left
 * unselected is **deactivated**, not deleted — they can be reactivated once
 * more seats are bought or the plan is upgraded.
 *
 * The API applies no admin protection: any user may be left out, including the
 * last administrator and the person doing the picking — and it reports no error
 * when that happens, so the account owner would find themselves deactivated by
 * their own purchase. This dialog keeps their own row selected and locked,
 * which is the only place that rule can be enforced.
 */
const CustomSeatReductionDialog = ({
  reduction,
  purchasedSeats,
  isSubmitting = false,
  onConfirm,
  onClose,
}: CustomSeatReductionDialogProps) => {
  const { userId, email } = useAppSelector((state) => state.auth);
  const users = useMemo(() => reduction?.users ?? [], [reduction]);
  const planCap = reduction?.keepCount ?? 0;

  /**
   * The row belonging to whoever is doing the picking. Matched on id, with the
   * email as the fallback — `userId` is cleared to `""` on logout and some
   * entry points restore it late, and a missed match here costs the customer
   * their own access.
   */
  const selfId = useMemo(() => {
    const ownEmail = String(email ?? "").trim().toLowerCase();
    const own = users.find(
      (user) =>
        (Number(userId) > 0 && user.id === Number(userId)) ||
        (Boolean(ownEmail) &&
          String(user.email ?? "").trim().toLowerCase() === ownEmail),
    );
    return own?.id;
  }, [users, userId, email]);
  /**
   * How many the customer may keep: the seats they're paying for, and never
   * more than the plan allows. The API reports the plan's cap, which is the
   * ceiling rather than the answer whenever fewer seats were bought.
   */
  /**
   * At least one, always. "Keep nobody" cannot be expressed: every backend
   * path reads an empty `keepUserIds` as *absent* and answers 409 again
   * (`keepUserIds == null || keepUserIds.isEmpty()`), so submitting an empty
   * list would just re-open this dialog. The seat count being bought can only
   * narrow the allowance the API reported, never widen it.
   */
  const keepCount =
    purchasedSeats && purchasedSeats > 0
      ? Math.max(1, Math.min(planCap, purchasedSeats))
      : Math.max(1, planCap);
  const [selected, setSelected] = useState<number[]>([]);
  const [search, setSearch] = useState<string>("");

  useEffect(() => {
    // Nobody else is pre-selected: who keeps their access is the customer's
    // call, and a default of "the first N in the list" is an arbitrary one to
    // make on their behalf when everyone else gets deactivated. Their own seat
    // is the exception — leaving it out deactivates the person mid-purchase,
    // silently, so it starts ticked and cannot be unticked.
    setSelected(selfId ? [selfId] : []);
    setSearch("");
  }, [reduction, selfId]);

  useEffect(() => {
    // The allowance can shrink under a selection that's already made — a caller
    // reusing the same reduction with fewer seats bought. Trim rather than
    // leave an over-limit state the API would reject.
    setSelected((current) => {
      if (current.length <= keepCount) return current;
      // Trimming from the end would drop the picker's own seat when it isn't
      // the first pick, which is the one row this dialog guarantees.
      const trimmed = current.filter((id) => id !== selfId);
      return selfId
        ? [selfId, ...trimmed.slice(0, Math.max(0, keepCount - 1))]
        : trimmed.slice(0, keepCount);
    });
  }, [keepCount, selfId]);

  /**
   * Ticking stops at `keepCount` — the seats the target plan allows. The API
   * rejects anything other than exactly that many, so the control never lets a
   * selection get there.
   */
  const toggle = (id: number) => {
    // Their own seat is not theirs to give up here: the API deactivates
    // everyone off the list and reports nothing, so releasing it would lock the
    // customer out of the account they are paying for.
    if (id === selfId) return;
    setSelected((current) =>
      current.includes(id)
        ? current.filter((value) => value !== id)
        : current.length >= keepCount
          ? current
          : [...current, id],
    );
  };

  // Keeping fewer than the seats bought is allowed — the API only rejects
  // keeping *more* — so the confirm needs at least one, not exactly N.
  // Submittable: at least one person, and never more than the allowance. The
  // API rejects keeping *more*, and reads an empty list as no answer at all.
  const isValidSelection =
    selected.length > 0 && selected.length <= keepCount;
  const isAtCapacity = selected.length === keepCount;
  /**
   * Everyone listed holds a seat — Active and invited alike — and everyone left
   * unselected is deactivated. Count off the roster rather than `activeCount`
   * so the disclosed impact can't understate a destructive action if the two
   * ever disagree.
   */
  const seatHolders = users.length || (reduction?.activeCount ?? 0);
  const deactivateCount = Math.max(0, seatHolders - selected.length);

  // Filtering never touches the selection: someone picked, then searched away,
  // still counts towards the total.
  const term = search.trim().toLowerCase();
  const visible = useMemo(
    () =>
      term
        ? users.filter((user) =>
            [user.name, user.username, user.email]
              .filter(Boolean)
              .some((field) => String(field).toLowerCase().includes(term)),
          )
        : users,
    [users, term],
  );

  const getRow = (user: SeatReductionUser) => {
    const isSelf = user.id === selfId;
    const isSelected = selected.includes(user.id);
    // Their own row is locked on rather than locked out, so it reads as a seat
    // already accounted for instead of one they've been refused.
    const isDisabled = isSelf || (!isSelected && selected.length >= keepCount);
    const label = user.name || user.username;
    return (
      <Box
        component="label"
        key={user.id}
        sx={rowSx(isSelected, isDisabled, isSelf)}
      >
        {/* A real <label> wrapping the input: one click, one toggle, and the
            whole row is the hit area without any propagation juggling. */}
        <Checkbox
          size="small"
          sx={isSelf ? lockedCheckboxSx : checkboxSx}
          checked={isSelected}
          disabled={isDisabled}
          onChange={() => toggle(user.id)}
          inputProps={{
            "aria-label": isSelf
              ? `${label} (you) keeps a seat — this cannot be changed`
              : `Keep ${label} (${statusLabel(user)})`,
          }}
        />
        <Box component="span" sx={avatarSx} aria-hidden="true">
          {initials(user)}
        </Box>
        <Box sx={{ minWidth: 0, flex: 1 }}>
          <Box component="span" sx={nameSx} title={label}>
            {isSelf ? `${label} (you)` : label}
          </Box>
          <Box component="span" sx={emailSx} title={user.email}>
            {user.email}
          </Box>
        </Box>
        <Box component="span" sx={statusSx(user.status)}>
          {statusLabel(user)}
        </Box>
      </Box>
    );
  };

  return (
    <AppModal
      open={Boolean(reduction)}
      onClose={onClose}
      title="Choose who to keep"
      // A picker over a user list — the design system's L width.
      size="lg"
      busy={isSubmitting}
      primaryLabel={`Keep ${selected.length} & deactivate ${deactivateCount}`}
      onPrimary={() => onConfirm(selected)}
      primaryDisabled={!isValidSelection}
      primaryLoading={isSubmitting}
      cancelLabel="Cancel"
      onCancel={onClose}
    >
        <Box>
          <Box sx={introSx}>
            {`${
              keepCount < planCap
                ? `You're buying ${keepCount} ${
                    keepCount === 1 ? "seat" : "seats"
                  } (this plan allows up to ${planCap})`
                : `This plan allows ${keepCount} ${
                    keepCount === 1 ? "user" : "users"
                  }`
            }, and ${seatHolders} ${
              seatHolders === 1 ? "person holds" : "people hold"
            } a seat (active or invited). Choose who keeps one — you can keep fewer than you bought, and anyone left out is deactivated until you bring them back.${
              selfId
                ? " Your own seat is kept for you, so you don't lock yourself out."
                : ""
            }`}
          </Box>

          {/* The whole summary is the live region: the count is the thing a
              screen-reader user is tracking as they tick, not just the hint. */}
          <Box sx={summarySx} aria-live="polite" aria-atomic="true">
            <Box sx={summaryRowSx}>
              {/* Green means "every seat filled", matching the meter — not
                  merely "submittable", which is true from the first tick. */}
              <Box component="span" sx={summaryCountSx(isAtCapacity)}>
                {`${selected.length} of ${keepCount} seats filled`}
              </Box>
              <Box component="span" sx={summaryHintSx}>
                {selected.length === 0
                  ? `pick up to ${keepCount}`
                  : `${deactivateCount} of ${seatHolders} will be deactivated`}
              </Box>
            </Box>
            <Box
              sx={meterTrackSx}
              role="progressbar"
              aria-valuenow={selected.length}
              aria-valuemin={0}
              aria-valuemax={keepCount}
              aria-label="Seats filled"
            >
              <Box
                sx={meterFillSx(
                  keepCount > 0 ? (selected.length / keepCount) * 100 : 0,
                  isAtCapacity,
                )}
              />
            </Box>
          </Box>

          {users.length >= SEARCHABLE_FROM && (
            <SettingsSearchField
              id="seat-reduction-search"
              placeholder="Search by name or email"
              ariaLabel="Search users to keep"
              onChange={(event) => setSearch(event.target.value)}
              sx={searchSx}
            />
          )}

          <Box sx={listSx}>
            {visible.length > 0 ? (
              visible.map(getRow)
            ) : (
              /*
                An empty roster isn't a failed search — quoting an empty term
                back at the reader reads like a bug, so the two cases say
                different things. `compact` is the widget size: no icon, and a
                single text link, which is all a list this size has room for.
              */
              <EmptyState
                size="compact"
                title={
                  search.trim()
                    ? `No one matches "${search.trim()}"`
                    : "No users to choose from"
                }
                /*
                  No "Clear search" action: `SettingsSearchField` takes no
                  `value`, so clearing the filter here would leave the typed
                  text sitting in the box. uat's original carried no action
                  either — this is a like-for-like conversion.
                */
                sx={emptyStateSx}
              />
            )}
          </Box>
        </Box>

      <Typography
        variant="caption"
        color="text.secondary"
        aria-live="polite"
        sx={{ mt: 1.5, display: "block" }}
      >
        {`Selected ${selected.length} of ${keepCount}`}
      </Typography>
    </AppModal>
  );
};

export default CustomSeatReductionDialog;
